import React, { useState, useEffect } from 'react';
import axios from 'axios';
import firebase from '../../Firebase/firebaseConnection';
import NodeBackend from '../../Resources/NodeBackend';
import './DeleteAccountModal.css';

const endpoint = new NodeBackend().getPath();
const DELETE_DRAFT_KEY = 'nextstock:pending-account-deletion';
export default function DeleteAccountModal({ email, onClose, onSignInAgain }) {
  const savedDraft = (() => { try { const d = JSON.parse(sessionStorage.getItem(DELETE_DRAFT_KEY) || 'null'); return d && d.email?.toLowerCase() === email?.toLowerCase() ? d : null; } catch { return null; } })();
  const [reason, setReason] = useState(savedDraft?.reason || '');
  const [typedEmail, setTypedEmail] = useState(savedDraft?.typedEmail || '');
  const [busy, setBusy] = useState(false);
  const [securityPrompt, setSecurityPrompt] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionState, setSubscriptionState] = useState('checking');
  useEffect(() => {
    let active = true;
    const check = async () => {
      setSubscriptionState('checking');
      try {
        const user = firebase.auth().currentUser;
        if (!user) throw new Error('Please sign in again.');
        const token = await user.getIdToken();
        const response = await axios.get(endpoint + 'delete-account/eligibility', {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        if (active) setSubscriptionState(response.data.eligible === true ? 'allowed' : 'blocked');
      } catch (e) {
        if (active) { setSubscriptionState('error'); setError('Could not verify your account status. Please close and reopen this window.'); }
      }
    };
    check();
    return () => { active = false; };
  }, []);
  const valid = reason.trim().length >= 3 && typedEmail.trim().toLowerCase() === email.trim().toLowerCase();
  async function returnToSignIn() {
    setBusy(true); setError('');
    try {
      const user = firebase.auth().currentUser;
      if (!user) throw new Error('Please sign in again.');
      const token = await user.getIdToken();
      const response = await axios.post(endpoint + 'delete-account/resume', {email:typedEmail.trim(),reason:reason.trim()}, {headers:{Authorization:`Bearer ${token}`}});
      const code = response.data.code;
      sessionStorage.setItem(DELETE_DRAFT_KEY, JSON.stringify({uid:user.uid,email:user.email || email,reason,typedEmail,pending:true,code}));
      await firebase.auth().signOut();
      ['userUid','userEmail','userName','userStatus','userTrial','loginExpiration'].forEach(k => localStorage.removeItem(k));
      const url = new URL(window.location.href);
      url.searchParams.set('page','Signin');
      url.searchParams.set('deletion_request',code);
      window.location.assign(url.pathname + url.search);
    } catch(e) {setError(e.response?.data?.error || e.message || 'Could not prepare secure sign-in.');setBusy(false);setSecurityPrompt(false);}
  }
  async function submit() {
    if (!valid || busy || subscriptionState !== 'allowed') return;
    setBusy(true); setError('');
    try {
      const user = firebase.auth().currentUser;
      if (!user) throw new Error('Please sign in again.');
      // Firebase ID tokens can remain valid long after the original sign-in.
      // The server deliberately requires auth_time within the last five minutes.
      const tokenInfo = await user.getIdTokenResult();
      const signedInAt = Date.parse(tokenInfo.authTime || '');
      if (!Number.isFinite(signedInAt) || Date.now() - signedInAt > 4 * 60 * 1000) {
        setBusy(false);
        setSecurityPrompt(true);
        return;
      }
      const token = await user.getIdToken(true);
      await axios.post(endpoint + 'delete-account', { email: typedEmail.trim(), reason: reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      await firebase.auth().signOut();
      sessionStorage.removeItem(DELETE_DRAFT_KEY);
      localStorage.removeItem('userUid');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      window.location.assign(window.location.pathname + '?page=Signin');
    } catch (e) {
      if (e.response?.data?.code === 'auth/requires-recent-login') {
        setBusy(false);
        setSecurityPrompt(true);
        return;
      } else {
        setError(e.response?.data?.error || e.message || 'Unable to delete account.');
      }
      setBusy(false);
    }
  }
  return <div className="ns-delete-backdrop" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
    <div className="ns-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ns-delete-title">
      <button type="button" className="ns-delete-x" onClick={onClose} disabled={busy} aria-label="Close">×</button>
      {securityPrompt && <div className="ns-security-overlay" role="dialog" aria-modal="true" aria-label="Verify your identity">
        <div className="ns-security-panel"><div className="ns-security-shield">✦</div><h2>One more step to protect your account</h2>
          <p>For your security, we need to verify your identity before permanently deleting your Next Stock account.</p>
          <p>You'll sign in again using your usual method. We'll then take you straight back to Settings, with your deletion request and reason preserved.</p>
          <div className="ns-security-actions"><button type="button" disabled={busy} onClick={()=>setSecurityPrompt(false)}>Not now</button><button type="button" disabled={busy} onClick={returnToSignIn}>{busy?'Preparing secure login…':'Continue securely →'}</button></div>
        </div></div>}
      <div className="ns-delete-icon">!</div>
      <h2 id="ns-delete-title">Delete your account?</h2>
      <p>This action is permanent. Your Next Stock account and access will be deleted.</p>
      <p className="ns-delete-warning">We will retain only your email address and your reason for deletion. If you sign up again with the same email, you will not receive another free trial month.</p>
      {subscriptionState === 'blocked' && <div className="ns-delete-subscription-block" role="alert">
        <span className="ns-delete-subscription-alert-icon" aria-hidden="true">⚠</span>
        <div><strong>Active Premium subscription — account deletion blocked</strong>
          <p>You must cancel your subscription first. Open <span className="ns-delete-nowrap"><b>$ Subscription</b></span> in the navigation menu, scroll to the last option, <span className="ns-delete-nowrap"><b>✕ Cancel your subscription</b></span>, and complete the cancellation. Then return to <b>Settings</b> to delete your account.</p>
        </div>
      </div>}
      {subscriptionState === 'checking' && <div className="ns-delete-checking" role="status" aria-live="polite">
        <span className="ns-delete-orbit" aria-hidden="true"><i/><i/><i/></span>
        <span><strong>Checking your account</strong><small>Securely verifying subscription eligibility…</small></span>
      </div>}

      <label htmlFor="ns-delete-reason">Why are you leaving? <span>*</span></label>
      <textarea id="ns-delete-reason" maxLength={3000} value={reason} onChange={e => setReason(e.target.value)} placeholder="Please tell us why you're deleting your account" />
      <label htmlFor="ns-delete-email">Type your full email to confirm <span>*</span></label>
      <input id="ns-delete-email" type="email" autoComplete="off" value={typedEmail} onChange={e => setTypedEmail(e.target.value)} placeholder={email} />
      {error && <p className="ns-delete-error" role="alert">{error}</p>}
      <div className="ns-delete-actions"><button type="button" onClick={onClose} disabled={busy}>Cancel</button><button type="button" className="ns-delete-confirm" disabled={!valid || busy || subscriptionState !== 'allowed'} onClick={submit}>{busy ? 'Deleting…' : 'Permanently delete account'}</button></div>
    </div>
  </div>;
}
